import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  Button,
  Typography,
} from "@mui/material";
import { Cancel } from "@mui/icons-material";
import LiveInfoCards from "../LiveInfoCards";
import DatePeriodSelector from "../DatePeriodSelector";
import MetricsCards from "../MetricsCards";
import MessagesChart from "../MessagesChart";
import styles from "../Dashboard.module.css";
import { useDashboard } from "../../../hooks/useDashboard";
import { useNotificationContext } from "../../../contextProviders/NotificationProvider";

export default function DashboardHome() {
  const [selectedPeriod, setSelectedPeriod] = useState("7 days");
  const [data, setData] = useState();
  const [onlineOperators, setOnlineOperators] = useState(0);
  const [showDateDialog, setShowDateDialog] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const { showNotification } = useNotificationContext();
  const { getDashboardChartAndCardData } = useDashboard();

  const [customRangeApplied, setCustomRangeApplied] = useState(false);
  async function fetchData() {
    const customRange =
      selectedPeriod === "Custom" ? { start: startDate, end: endDate } : null;

    const result = await getDashboardChartAndCardData(
      selectedPeriod,
      customRange
    );
    setData(result);
    // Initialize online count from API (will be overridden by SignalR events)
    if (result?.onlineOperators !== undefined) {
      setOnlineOperators(result.onlineOperators);
    }
  }
  useEffect(() => {
    if (selectedPeriod !== "Custom" || customRangeApplied) {
      fetchData();
    }
  }, [selectedPeriod, customRangeApplied]);

  // Listen for real-time online operator count from SignalR
  useEffect(() => {
    const handleOnlineCount = (e) => {
      setOnlineOperators(e.detail ?? 0);
    };
    window.addEventListener('signalr-online-operator-count', handleOnlineCount);
    return () => window.removeEventListener('signalr-online-operator-count', handleOnlineCount);
  }, []);

  const handlePeriodChange = (period) => {
    if (period === "Custom") {
      setShowDateDialog(true);
    } else {
      setSelectedPeriod(period);
      setCustomRangeApplied(false); // reset flag
    }
  };

  const handleApplyDateRange = () => {
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const today = new Date();

      if (start > end) {
        showNotification("Start date must be before end date", "warning");
        return;
      }
    

      setSelectedPeriod("Custom");
      setCustomRangeApplied(true); // trigger fetch
      setShowDateDialog(!customRangeApplied);
      fetchData();
    } else {
      showNotification("Both start and end dates are required", "warning");
    }
    setShowDateDialog(false);
  };

  const liveData = data
    ? {
        loggedOnOps: data.availableOperators,
        onlineOperators: onlineOperators ?? data.onlineOperators ?? 0,
        messagesInQueue: data.messagesInQueue,
        overdueReplies: data.overdueReplies,
      }
    : {};

  const metricsData = data
    ? {
        totalPurchases: data.totalPurchases,
        totalMessagesSent: data.totalMessagesSent,
        totalMessagesReceived: data.totalMessagesReceived,
        newConsumersRegistered: data.newConsumersRegistered,
        replyTimeOver15Min: data.replyWithin15Minutes,
      }
    : {};

  return (
    <>
      <h1 className={styles.dashboardTitle}>DASHBOARD</h1>
      <LiveInfoCards data={liveData} />
      <DatePeriodSelector
        selectedPeriod={selectedPeriod}
        onPeriodChange={handlePeriodChange}
      />
      <MetricsCards data={metricsData} />
      <MessagesChart
        period={selectedPeriod}
        startDate={startDate}
        endDate={endDate}
        data={data?.chartData || []}
      />

      {/* Inline Custom Date Range Dialog */}
      <Dialog
        open={showDateDialog}
        onClose={() => setShowDateDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            minWidth: 300,
            maxWidth: 450,
            p: 1,
            position: "relative",
          },
        }}
      >
        <IconButton
          aria-label="close"
          onClick={() => setShowDateDialog(false)}
          size="small"
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "var(--color-close-icon)",
            zIndex: 1,
          }}
        >
          <Cancel fontSize="small" />
        </IconButton>
        <DialogTitle sx={{ pr: 4 }}>
          <Typography
            variant="body2"
            fontWeight="bold"
            color="var(--color-primary)"
          >
            {"Select Date Range".toUpperCase()}
          </Typography>
        </DialogTitle>
        <DialogContent
          className={styles.dialogContent}
          style={{ paddingTop: 5 }}
        >
          <TextField
            label="Start Date"
            type="date"
            fullWidth
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{
      max: new Date().toISOString().split("T")[0], 
    }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="End Date"
            type="date"
            fullWidth
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
       
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            fullWidth
            onClick={handleApplyDateRange}
            sx={{
              backgroundColor: "var(--color-primary)",
              "&:hover": { backgroundColor: "var(--color-secondary)" },
            }}
          >
            Apply
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
